import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertCircle, X, Loader2 } from 'lucide-react';
import { useQuotes } from '../hooks/useQuotes';
import { useAuth } from '../lib/AuthContext';

const QUOTE_STYLES = [
  { 
    id: 'typewriter', 
    name: 'Typewriter', 
    previewImage: 'https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/typewriter.png',
    className: 'font-typewriter'
  },
  { 
    id: 'kristi', 
    name: 'Kristi', 
    previewImage: 'https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/kristi.png',
    className: 'font-kristi'
  },
  { 
    id: 'windsong', 
    name: 'WindSong', 
    previewImage: 'https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/windsong.png',
    className: 'font-windsong'
  }
];

export default function SendQuoteStylePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { quotes } = useQuotes();
  const { user } = useAuth();
  const [selectedStyle, setSelectedStyle] = useState(QUOTE_STYLES[0].id);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);

  const quote = quotes.find(q => q.id === id);

  if (!quote) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Quote not found</div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    setSuccess(false);
    setDebugInfo(null);

    try {
      const userEmail = user?.email || '';
      const usernamePrefix = userEmail.split('@')[0];

      const payload = {
        quoteId: quote.id,
        text: quote.text,
        author: quote.author,
        style: selectedStyle,
        userEmail,
        usernamePrefix
      };

      console.log('Sending payload:', payload);
      setDebugInfo(`Sending request to webhook...\nPayload: ${JSON.stringify(payload, null, 2)}`);

      const response = await fetch('https://hook.us1.make.com/yuq3514axh9432t2ki64pjynh76d0llz', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);
      setDebugInfo(prev => `${prev}\nResponse status: ${response.status}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json().catch(() => {
        console.log('Response is not JSON');
        return {};
      });

      console.log('Response data:', data);
      setDebugInfo(prev => `${prev}\nResponse received successfully\nData: ${JSON.stringify(data, null, 2)}`);
      
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/quotes');
      }, 2000);
    } catch (err) {
      console.error('Failed to send quote:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send quote. Please try again.';
      setError(errorMessage);
      setDebugInfo(prev => `${prev}\nError: ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Quotes</span>
        </button>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Send Quote with Style</h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-700 hover:text-red-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-md flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <span>Quote sent successfully! Redirecting...</span>
            </div>
          )}

          {debugInfo && (
            <div className="mb-6 p-4 bg-gray-50 text-gray-700 rounded-md">
              <pre className="whitespace-pre-wrap text-sm font-mono">{debugInfo}</pre>
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Selected Quote</h2>
            <div className="p-4 bg-gray-50 rounded-md">
              <p className="text-lg text-gray-800 italic">"{quote.text}"</p>
              <p className="mt-2 text-gray-600">— {quote.author}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Choose a Style
              </label>
              <div className="grid grid-cols-1 gap-6">
                {QUOTE_STYLES.map((style) => (
                  <div
                    key={style.id}
                    className={`p-6 border rounded-lg cursor-pointer transition-all ${
                      selectedStyle === style.id 
                        ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedStyle(style.id)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">{style.name}</h3>
                      <div className={`w-6 h-6 rounded-full border-2 ${
                        selectedStyle === style.id 
                          ? 'border-blue-500 bg-blue-500' 
                          : 'border-gray-300'
                      }`}>
                        {selectedStyle === style.id && (
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <img
                        src={style.previewImage}
                        alt={`${style.name} font style`}
                        className="h-12 object-contain"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  'Send Quote'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}